import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../components/Button';
import { Flex } from '../../../../components/Flex';
import { Input } from '../../../../components/Input';
import { Text } from '../../../../components/Text';
import { Modal } from '../../../../components/modal/Modal';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { userSliceSelectors } from '../../../../redux/user/user.selector';
import { userActions } from '../../../../redux/user/user.slice';
import { AtiraToast } from '../../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../../utils/onSetTypedCRUDErrors';

type SettingsEmailChangeNewEmailModalProps = {
  open: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

export const SettingsEmailChangePassword: React.FC<
  SettingsEmailChangeNewEmailModalProps
> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const { control, watch, handleSubmit, reset, setError } = useForm<{
    password: string;
  }>();
  const password = watch('password');

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onLocalClose = () => {
    onClose();
    reset({ password: '' });
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      await dispatch(
        userActions.updateUserEmailPassword({ userId, password }),
      ).unwrap();

      reset({ password: '' });
      onSuccess();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onLocalClose}
      title={t('settings.general.changeEmail.password.modal.title')}
    >
      <Flex width={'100%'} flexDirection="column" gap="m">
        <Text color="fgSecondary" align="center">
          {t('settings.general.changeEmail.password.modal.description')}
        </Text>

        <Controller
          control={control}
          name="password"
          rules={{
            required: {
              value: true,
              message: t('common.this_field_required'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              value={value}
              onChange={onChange}
              title={t(
                'settings.general.changeEmail.password.modal.input.title',
              )}
              type="password"
              required
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />

        <Button
          onClick={handleSubmit(onSubmit, (e) => console.log(e))}
          disabled={!password?.length}
          height="2.3rem"
          loading={loading}
        >
          {t('settings.general.changeEmail.password.modal.button')}
        </Button>
      </Flex>
    </Modal>
  );
};
